import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {TagBehaviorsEnum} from '../../../../../../libs/shared/core/src/models/api/commonTagModel';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {InterfaceEventsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ResourceMap} from '../../../../../../libs/shared/core/src/store/commonAppTypes';
import {getTeammates} from '../../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {ellipsis, flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {tagHasBehaviors} from '../../../../../helpers/models/tagHelpers';
import {AnalyticsDetailsConversation} from '../../../../../models/api/analyticsMetricDetailsModel';
import {getInboxes} from '../../../../../store/data/inboxes/inboxesSelectors';
import {setSelectedConversationAction} from '../../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getAnalyticsDetailsSelectedMetricWithOptions} from '../../../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {useAnalyticsTracking} from '../../../../helpers/tracking/useAnalyticsTracking';
import {sidePanelConversationCard} from '../../../reportViewer/reportCommons';
import {ConversationCardInbox} from './conversationCardInbox';
import {ConversationCardLastActivity} from './conversationCardLastActivity';
import {ConversationCardStatus} from './conversationCardStatus';
import {ConversationCardSubject} from './conversationCardSubject';
import {ConversationCardTag} from './conversationCardTag';
import {ConversationCardTeammate} from './conversationCardTeammate';

/*
 * Const.
 */

const GRID_GAP = 4;

/*
 * Props.
 */

interface ConversationCardProps {
  conversation: AnalyticsDetailsConversation;
  isInteractive: boolean;
}

/*
 * Styles.
 */

const StyledConversationCardWrapper = styled(ChromelessButton)<{$isInteractive: boolean}>`
  ${sidePanelConversationCard}

  display: grid;

  grid-row-gap: ${GRID_GAP}px;
  grid-template-rows: repeat(3, auto);

  transition: background-color 0.1s;

  ${(p) =>
    p.$isInteractive &&
    css`
      &:hover {
        background-color: ${p.theme.greys.shade10};
      }
    `}
`;

const StyledFirstRow = styled.div`
  display: grid;
  grid-column-gap: ${GRID_GAP}px;
  grid-template-columns: 1fr auto 20px;
  grid-template-areas: 'inboxes status teammate';
`;

const StyledSecondRow = styled.div`
  display: grid;
  grid-column-gap: ${GRID_GAP}px;
  grid-template-areas: 'subject last-activity';
`;

const StyledThirdRow = styled.div`
  display: grid;
  /* Tags are not always there so setting min-height to make sure the row is always there even without tags. */
  min-height: 16px;
  grid-column-gap: ${GRID_GAP}px;
  grid-template-areas: 'tags';
`;

const StyledConvInboxes = styled.div`
  grid-area: inboxes;
  ${flexCenter}
  ${ellipsis()}
`;

const StyledConvTags = styled.div`
  grid-area: tags;
  ${flexCenter}
  white-space: nowrap;
  overflow-x: hidden;
`;

const ConversationCardTagWrapper = styled.div`
  margin-right: 4px;
`;

/*
 * Component.
 */

export const ConversationCard: FC<ConversationCardProps> = ({conversation, isInteractive}) => {
  const dispatch = useAppDispatch();

  const trackAnalytics = useAnalyticsTracking();

  const inboxes = useAppSelector(getInboxes);
  const teammates = useAppSelector(getTeammates);
  const {selectedMetric, options} = useAppSelector(getAnalyticsDetailsSelectedMetricWithOptions);

  const trackAndOpenPopup = (convId: number) => {
    if (!isInteractive) {
      return;
    }

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DETAILS_DRAWER_CLICK, {
      metric_type: selectedMetric,
      contributing_segments_options: options,
      conversation_id: convId,
    });

    dispatch(setSelectedConversationAction(conversation));
  };

  return (
    <StyledConversationCardWrapper
      onClick={() => trackAndOpenPopup(conversation.id)}
      $isInteractive={isInteractive}
    >
      {renderFirstRow(conversation, inboxes, teammates)}
      {renderSecondRow(conversation)}
      {renderThirdRow(conversation)}
    </StyledConversationCardWrapper>
  );
};

/*
 * Helpers.
 */

// Inbox, Status and Assigned Teammate.
function renderFirstRow(
  conversation: AnalyticsDetailsConversation,
  inboxes: ResourceMap<InboxModel>,
  teammates: ResourceMap<TeammateModel>,
) {
  return (
    <StyledFirstRow>
      <StyledConvInboxes>
        {conversation.inboxIds.map((id) => {
          const inbox = inboxes[id];

          if (!inbox) {
            return null;
          }

          return <ConversationCardInbox key={id} inbox={inbox} />;
        })}
      </StyledConvInboxes>
      <ConversationCardStatus conversation={conversation} />
      <ConversationCardTeammate
        teammate={conversation.assigneeId ? teammates[conversation.assigneeId] : undefined}
      />
    </StyledFirstRow>
  );
}

// Conversation subject and Last activity.
function renderSecondRow(conversation: AnalyticsDetailsConversation) {
  return (
    <StyledSecondRow>
      <ConversationCardSubject subject={conversation.subject || conversation.summary} />
      <ConversationCardLastActivity lastActivityAt={conversation.lastActivityAt} />
    </StyledSecondRow>
  );
}

// Tags.
function renderThirdRow(conversation: AnalyticsDetailsConversation) {
  return (
    <StyledThirdRow>
      <StyledConvTags>
        {conversation.tags
          .filter((tag) => tagHasBehaviors(tag, TagBehaviorsEnum.CAN_DISPLAY))
          .map((tag) => (
            <ConversationCardTagWrapper key={tag.id}>
              <ConversationCardTag tag={tag} />
            </ConversationCardTagWrapper>
          ))}
      </StyledConvTags>
    </StyledThirdRow>
  );
}
